import React, { useEffect, useMemo, useState } from "react";
import { fetchWithAuth } from "../config/api";

const blankItem = {
  name: "",
  category_id: "",
  category_name: "",
  item_type: "product",
  sell_price: "",
  cost_price: "",
  quantity: "",
  is_active: true,
};

function money(value) {
  return Number(value || 0).toFixed(2);
}

export default function ItemManager() {
  const [items, setItems] = useState([]);
  const [categories, setCategories] = useState([]);
  const [form, setForm] = useState(blankItem);
  const [editingId, setEditingId] = useState(null);
  const [status, setStatus] = useState("");
  const [loading, setLoading] = useState(false);
  const [showInactive, setShowInactive] = useState(false);

  const groupedItems = useMemo(() => {
    return items.reduce((acc, item) => {
      const key = item.category || "Uncategorized";
      acc[key] = acc[key] || [];
      acc[key].push(item);
      return acc;
    }, {});
  }, [items]);

  async function load() {
    setLoading(true);
    setStatus("");
    try {
      const [itemRes, categoryRes] = await Promise.all([
        fetchWithAuth(`/api/items/${showInactive ? "?include_inactive=1" : ""}`),
        fetchWithAuth("/api/items/categories/"),
      ]);
      if (itemRes.ok) setItems(await itemRes.json());
      if (categoryRes.ok) setCategories(await categoryRes.json());
    } catch (err) {
      console.error("load items failed", err);
      setStatus("Failed to load items.");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showInactive]);

  function updateForm(field, value) {
    setForm((prev) => ({ ...prev, [field]: value }));
  }

  function startEdit(item) {
    setEditingId(item.id);
    setForm({
      name: item.name || "",
      category_id: item.category_id || "",
      category_name: "",
      item_type: item.item_type || "product",
      sell_price: item.sell_price ?? "",
      cost_price: item.cost_price ?? "",
      quantity: item.quantity ?? "",
      is_active: item.is_active !== false,
    });
  }

  function resetForm() {
    setEditingId(null);
    setForm(blankItem);
  }

  async function saveItem(e) {
    e.preventDefault();
    setStatus("");
    const payload = {
      ...form,
      sell_price: Number(form.sell_price || 0),
      cost_price: Number(form.cost_price || 0),
      quantity: Number(form.quantity || 0),
      category_id: form.category_id || null,
      category_name: form.category_name || "",
    };

    const res = await fetchWithAuth(
      editingId ? `/api/items/${editingId}/` : "/api/items/",
      {
        method: editingId ? "PATCH" : "POST",
        body: JSON.stringify(payload),
      }
    );

    if (!res.ok) {
      const js = await res.json().catch(() => ({}));
      setStatus(js.error || "Save failed.");
      return;
    }

    setStatus(editingId ? "Item updated." : "Item created.");
    resetForm();
    load();
  }

  async function toggleItem(item) {
    const res = await fetchWithAuth(`/api/items/${item.id}/`, {
      method: "PATCH",
      body: JSON.stringify({ is_active: !item.is_active }),
    });
    if (res.ok) load();
  }

  async function removeItem(item) {
    const res = await fetchWithAuth(`/api/items/${item.id}/`, { method: "DELETE" });
    if (res.ok) load();
  }

  async function createCategory() {
    const name = form.category_name.trim();
    if (!name) return;
    const res = await fetchWithAuth("/api/items/categories/", {
      method: "POST",
      body: JSON.stringify({ name }),
    });
    if (!res.ok) return;
    const category = await res.json();
    setCategories((prev) => [...prev.filter((c) => c.id !== category.id), category]);
    setForm((prev) => ({ ...prev, category_id: category.id, category_name: "" }));
  }

  return (
    <div className="card">
      <h3>Retail Items</h3>

      <form
        onSubmit={saveItem}
        style={{
          display: "grid",
          gridTemplateColumns: "1.5fr 1fr 1fr 1fr 1fr 1fr auto",
          gap: 8,
          alignItems: "end",
          marginBottom: 12,
        }}
      >
        <label className="label">
          Item Name
          <input
            className="input"
            value={form.name}
            onChange={(e) => updateForm("name", e.target.value)}
            required
            style={{ display: "block", width: "100%" }}
          />
        </label>
        <label className="label">
          Category
          <select
            className="input"
            value={form.category_id}
            onChange={(e) => updateForm("category_id", e.target.value)}
            style={{ display: "block", width: "100%" }}
          >
            <option value="">None</option>
            {categories.map((category) => (
              <option key={category.id} value={category.id}>
                {category.name}
              </option>
            ))}
          </select>
        </label>
        <label className="label">
          New Category
          <input
            className="input"
            value={form.category_name}
            onChange={(e) => updateForm("category_name", e.target.value)}
            style={{ display: "block", width: "100%" }}
          />
        </label>
        <label className="label">
          Sell
          <input
            className="input"
            type="number"
            min="0"
            step="0.01"
            value={form.sell_price}
            onChange={(e) => updateForm("sell_price", e.target.value)}
            style={{ display: "block", width: "100%" }}
          />
        </label>
        <label className="label">
          Cost
          <input
            className="input"
            type="number"
            min="0"
            step="0.01"
            value={form.cost_price}
            onChange={(e) => updateForm("cost_price", e.target.value)}
            style={{ display: "block", width: "100%" }}
          />
        </label>
        <label className="label">
          Quantity
          <input
            className="input"
            type="number"
            value={form.quantity}
            onChange={(e) => updateForm("quantity", e.target.value)}
            style={{ display: "block", width: "100%" }}
          />
        </label>
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          <button className="btn" type="submit">
            {editingId ? "Update" : "Add"}
          </button>
          <button className="btn" type="button" onClick={createCategory}>
            Save Category
          </button>
          {editingId && (
            <button className="btn" type="button" onClick={resetForm}>
              Cancel
            </button>
          )}
        </div>
      </form>

      <div style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 12 }}>
        <label className="label" style={{ marginBottom: 0 }}>
          <input
            type="checkbox"
            checked={showInactive}
            onChange={(e) => setShowInactive(e.target.checked)}
            style={{ marginRight: 6 }}
          />
          Show inactive
        </label>
        <button className="btn" type="button" onClick={load} disabled={loading}>
          Refresh
        </button>
        {status && <span>{status}</span>}
      </div>

      {Object.keys(groupedItems).map((categoryName) => (
        <div key={categoryName} style={{ marginBottom: 16 }}>
          <h4>{categoryName}</h4>
          <div style={{ overflow: "auto", border: "1px solid #f3cfe0" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 760 }}>
              <thead>
                <tr>
                  <th style={{ textAlign: "left", padding: 6 }}>Item</th>
                  <th style={{ textAlign: "right", padding: 6 }}>Sell</th>
                  <th style={{ textAlign: "right", padding: 6 }}>Cost</th>
                  <th style={{ textAlign: "right", padding: 6 }}>Quantity</th>
                  <th style={{ textAlign: "left", padding: 6 }}>Status</th>
                  <th style={{ padding: 6 }} />
                </tr>
              </thead>
              <tbody>
                {groupedItems[categoryName].map((item) => (
                  <tr key={item.id}>
                    <td style={{ padding: 6 }}>{item.name}</td>
                    <td style={{ padding: 6, textAlign: "right" }}>{money(item.sell_price)}</td>
                    <td style={{ padding: 6, textAlign: "right" }}>{money(item.cost_price)}</td>
                    <td style={{ padding: 6, textAlign: "right" }}>{item.quantity}</td>
                    <td style={{ padding: 6 }}>{item.is_active ? "Active" : "Inactive"}</td>
                    <td style={{ padding: 6, textAlign: "right" }}>
                      <button className="btn" type="button" onClick={() => startEdit(item)}>
                        Edit
                      </button>{" "}
                      <button className="btn" type="button" onClick={() => toggleItem(item)}>
                        {item.is_active ? "Deactivate" : "Activate"}
                      </button>{" "}
                      <button className="btn" type="button" onClick={() => removeItem(item)}>
                        Remove
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ))}
    </div>
  );
}
